
import {
    DepositType,
    WithdrawalType,
} from '@core/models/user/enums';
import { maintenanceService } from '@superbet-group/web.lib.flags';
import { MaintenanceType } from '@superbet-group/web.types';
import Vue from 'vue';
import { Commit } from 'vuex';
import { shellLocalConfig } from '@src/config/shell/localConfig';

const SET_MAINTAINED_PARTS = 'SET_MAINTAINED_PARTS';

export interface MaintenanceState {
    maintainedParts: Record<string, boolean>;
}

export interface MaintenanceGetters {
    isInMaintenance: (part: MaintenanceType) => boolean;
}

const depositTypeToFlag = {
    [DepositType.Online]: 'online.wallet.onlineDeposit',
    [DepositType.Aircash]: 'online.wallet.aircashDeposit',
    [DepositType.BankTransfer]: 'online.wallet.bankTransferDeposit',
    [DepositType.Shop]: 'online.wallet.agencyDeposit',
    [DepositType.PaySafeCard]: 'online.wallet.paySafeCardDeposit',
    [DepositType.PaySpot]: 'online.wallet.paySpotDeposit',
    [DepositType.Transaks]: 'online.wallet.transaksDeposit',
    [DepositType.OnlinePix]: 'online.wallet.onlinePixDeposit',
    [DepositType.Bancontact]: 'online.wallet.bancontact',
    [DepositType.PaymentIQ]: 'online.wallet.onlinePiqDeposit',
} as Record<DepositType, string>;

const withdrawalTypeToFlag = {
    [WithdrawalType.Online]: 'online.wallet.onlineWithdrawal',
    [WithdrawalType.BankTransfer]: 'online.wallet.bankTransferWithdrawal',
    [WithdrawalType.Shop]: 'online.wallet.agencyWithdrawal',
    [WithdrawalType.PaySafeCard]: 'online.wallet.paySafeCardWithdrawal',
    [WithdrawalType.Transaks]: 'online.wallet.transaksWithdrawal',
    [WithdrawalType.OnlinePix]: 'online.wallet.onlinePixWithdrawal',
    [WithdrawalType.Instant]: 'online.wallet.instantWithdrawal',
    [WithdrawalType.PaymentIQ]: 'online.wallet.onlinePiqWithdrawal',
} as Record<WithdrawalType, string>;

const maintenanceStore = {
    namespaced: true,
    state: {
        maintainedParts: {},
    },
    getters: {
        maintainedParts(state: MaintenanceState) {
            return state.maintainedParts;
        },
        isInMaintenance(state: MaintenanceState) {
            return (part: MaintenanceType): boolean => {
                return state.maintainedParts[part];
            };
        },
        isDepositTypeAvailable(state: MaintenanceState) {
            return (type: DepositType): boolean => {
                const defaultFlagValue = shellLocalConfig.defaultDepositTypes[type];
                const maintenanceFlagValue = state.maintainedParts[depositTypeToFlag[type]];
                return maintenanceFlagValue === null || maintenanceFlagValue === undefined ? defaultFlagValue : !maintenanceFlagValue;
            };
        },
        isWithdrawalTypeAvailable(state: MaintenanceState) {
            return (type: WithdrawalType): boolean => {
                const defaultFlagValue = shellLocalConfig.defaultWithdrawalTypes[type];
                const maintenanceFlagValue = state.maintainedParts[withdrawalTypeToFlag[type]];
                return maintenanceFlagValue === null || maintenanceFlagValue === undefined ? defaultFlagValue : !maintenanceFlagValue;
            };
        },
    },
    mutations: {
        [SET_MAINTAINED_PARTS](state: MaintenanceState, changes: Record<string, boolean>) {
            Object.keys(changes).forEach((key) => Vue.set(state.maintainedParts, key, changes[key]));
        },
    },
    actions: {
        initialize({ commit }: { commit: Commit }) {
            return maintenanceService.subscribe((changes) => {
                if (window.location.hostname.includes('silent')) {
                    Object.keys(changes).forEach((key) => {
                        // eslint-disable-next-line no-param-reassign
                        changes[key] = false;
                    });
                }
                commit(SET_MAINTAINED_PARTS, changes);
            });
        },
    },
};

export type MaintenanceStore = typeof maintenanceStore;

export default maintenanceStore;
